import { useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { KeyboardShortcut } from "@/components/KeyboardShortcut";
import { Row } from "@/components/Row";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";

import { media } from "@/lib/media";

const videoLinkSchema = z.object({
  url: z
    .string()
    .min(1, { message: "Required" })
    .url({ message: "Must be a valid URL" })
    .refine(media.isYouTubeUrl, {
      message: "Must be a YouTube link (youtube.com or youtu.be)",
    }),
});

type VideoLinkFormValues = z.infer<typeof videoLinkSchema>;

export const VideoLinkDialog = ({
  onOpenChange,
  onSubmit,
  open,
}: {
  onOpenChange: (open: boolean) => void;
  onSubmit: (url: string) => void;
  open: boolean;
}) => {
  const form = useForm<VideoLinkFormValues>({
    resolver: zodResolver(videoLinkSchema),
    defaultValues: { url: "" },
  });

  // Reset form when dialog closes.
  useEffect(() => {
    if (!open) {
      form.reset();
    }
  }, [open, form]);

  const handleSubmit = (data: VideoLinkFormValues) => {
    onSubmit(data.url);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="text-2xl font-semibold text-slate-700">
            Video Link
          </DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form
            className="flex flex-col gap-4"
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onSubmit={form.handleSubmit(handleSubmit)}
            onKeyDown={(e) => {
              if (e.metaKey && e.key === "Enter") {
                e.preventDefault();
                void form.handleSubmit(handleSubmit)();
              }
            }}
          >
            <FormField
              control={form.control}
              name="url"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      {...field}
                      autoFocus
                      placeholder="Paste in youtube link"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button
                className="rounded-full"
                onClick={() => {
                  onOpenChange(false);
                }}
                type="button"
                variant="secondary"
              >
                Cancel
              </Button>
              <Button className="rounded-full" type="submit">
                <Row className="items-center">
                  Submit
                  <KeyboardShortcut text="⌘↵" />
                </Row>
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
